import ConditionalToolTip from "components/design/conditionalToolTip";
import Loader from "components/DesignSystem/Loader/loader";
import { TableUI } from "components/design/TableUI";
import { TableEmptyState } from "components/TableEmptyState/TableEmptyState";
import { EmptyHourGlass } from "components/icons/EmptyHourGlass";
import ToolTip from "components/design/toolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Search } from "components/DesignSystem/Search/Search";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ApplyToAllIcon } from "components/icons/ApplyToAllIcon";
import { BankLogo } from "components/icons/BankLogo";
import { DeleteIcon } from "components/icons/delete";
import { REQUESTED_INFO_CUSTOM_TYPE } from "constants/chatType";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { TRANSACTION_SOURCE } from "dictionaries";
import { useFormikContext } from "formik";
import { requestInfoValidation } from "formValidations/requestInfoValidation";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "utils/debouncing";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/store";
import { resetOpenItemFilter } from "store/slices/openItemFilter";
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import {
  OpenItemFilterParams,
  TransactionWithOpenItemInfo,
  useAddClarificationMutation,
  useAddOpenItemsMutation,
  useDeleteOpenItemTxnMutation,
  useGetOpenItemTransactionsQuery,
  useSendClarificationRequestMutation,
} from "store/apis/openItem";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { OpenItem } from "types/Models/openItem";
import { BackendError } from "types/utils/error";
import { TxnAmount } from "./TransactionColumn";
import OpenItemsFilter from "./OpenItemsFilter";

type RequestInfoProps = {
  isOpen: boolean;
  onClose: () => void;
  channelUrl: string;
  transactionIds: string;
  deselectRows: () => void;
  setRowSelection: (rowSelection: any) => void;
};

type TableMetaType = {
  setFocusedItem: (item: string | null) => void;
  focusedItem: string | null;
  setRowSelection: (rowSelection: any) => void;
  isLoading: boolean;
  isFetching: boolean;
  deletingRowId: string | null;
  setDeletingRowId: (id: string | null) => void;
};

export const DeleteOpenItem = (info: CellContext<OpenItem, string>) => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const [deleteOpenItemTxn, { isLoading }] = useDeleteOpenItemTxnMutation();
  const uuid = info.getValue();

  const deleteTxn = async (openItemId: string) => {
    try {
      await deleteOpenItemTxn({ groupId, openItemId }).unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };
  return (
    <Button
      customType="ghost_icon"
      onClick={() => deleteTxn(uuid)}
      disabled={isLoading}
      isLoading={isLoading}
    >
      {isLoading ? "" : <DeleteIcon />}
    </Button>
  );
};

const ApplyToAll = ({
  info,
}: {
  info: CellContext<TransactionWithOpenItemInfo, string | null>;
}) => {
  const { setValues, values } = useFormikContext<{
    information_requests: Record<string, string>;
  }>();

  const uuid = info.row.original.transaction.uuid;

  const copyTextToAll = () => {
    setValues({
      information_requests: Object.fromEntries(
        Object.keys(values.information_requests).map((k) => [
          k,
          values.information_requests[uuid],
        ])
      ),
    });
  };

  return (
    <ToolTip text="Apply to all">
      <Button
        type="button"
        customType="ghost_icon"
        size="small"
        onClick={copyTextToAll}
      >
        <span className="t-text-text-30">
          <ApplyToAllIcon />
        </span>
      </Button>
    </ToolTip>
  );
};

const RequestInput = (
  info: CellContext<TransactionWithOpenItemInfo, string | null | undefined>
) => {
  const { isAdmin, isCpa, isForeignCA } = useRoleBasedView();
  const informationRequest = info.getValue();
  const uuid = info.row.original.transaction.uuid;

  return (
    <ConditionalToolTip
      condition={
        informationRequest &&
        "A request has already been sent for this transaction."
      }
    >
      <div>
        <TextArea
          size="small"
          name={`information_requests.${uuid}`}
          disabled={!Boolean(isAdmin || isCpa || isForeignCA)}
          cols={100}
          rows={1}
          id={uuid}
          onFocus={() =>
            // @ts-ignore
            info.table.options.meta?.setFocusedItem(uuid)
          }
          placeholder="Enter information..."
        />
      </div>
    </ConditionalToolTip>
  );
};

const DeleteColumn = ({
  row,
  meta,
}: {
  row: TransactionWithOpenItemInfo;
  meta: TableMetaType;
}) => {
  const { setRowSelection, deletingRowId, setDeletingRowId, isFetching } = meta;
  const uuid = row.transaction.uuid;
  const isDeleting = deletingRowId === uuid;

  const handleRowDelete = (transactionId: string) => {
    setDeletingRowId(transactionId);
    setRowSelection((prev: RowSelectionState) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => !id.includes(transactionId))
      )
    );
  };

  if (!isFetching) setDeletingRowId(null);

  return (
    <Button
      type="button"
      customType="ghost_icon"
      onClick={() => handleRowDelete(uuid)}
      disabled={isDeleting && isFetching}
      isLoading={isDeleting && isFetching}
    >
      <DeleteIcon />
    </Button>
  );
};

const columnHelper = createColumnHelper<TransactionWithOpenItemInfo>();

const columns = [
  columnHelper.accessor((row) => row.transaction, {
    id: "Date",
    enableSorting: true,
    cell: (info) => {
      const { date } = info.getValue();
      return (
        <div className="t-text-subtext t-text-text-30 t-whitespace-nowrap">
          {dayjs(date).format(DISPLAY_DATE)}
        </div>
      );
    },
    header: "Date",
    size: 8,
  }),
  columnHelper.accessor((row) => row.transaction, {
    id: "Vendor",
    enableSorting: true,
    cell: (info) => {
      const { merchant, logo } = info.getValue();
      if (!Boolean(merchant)) return "-";
      return (
        <div className="t-flex t-gap-2 t-items-center">
          <Avatar src={logo || ""} alt={merchant!} />
          <div className="t-text-subtext t-text-text-60">{merchant}</div>
        </div>
      );
    },
    header: "Vendor",
    size: 10,
  }),
  columnHelper.accessor((row) => row.transaction.from, {
    id: "Source",
    cell: (info) => {
      const from = info.getValue();
      if (!from) return <span className="t-text-text-30">-</span>;

      const { bank_account } = from;
      const { bank_brand } = bank_account || {};
      const { logo_url } = bank_brand || {};
      const mask = bank_account?.mask ? `•••• ${bank_account.mask}` : "";

      return (
        <ConditionalToolTip
          condition={`${bank_account?.nickname || ""} ${mask} (${
            TRANSACTION_SOURCE[from.source!]
          })`}
        >
          <div className="t-flex t-items-center t-justify-center">
            {logo_url ? (
              <Avatar src={logo_url} alt={bank_account?.mask || "Bank"} />
            ) : (
              <BankLogo />
            )}
          </div>
        </ConditionalToolTip>
      );
    },
    header: () => <div className="t-text-center">Source</div>,
    enableSorting: false,
    size: 5,
  }),
  columnHelper.accessor((row) => row.transaction.description, {
    id: "Description",
    enableSorting: true,
    cell: (info) => {
      const description = info.getValue();
      return (
        <ConditionalToolTip condition={description} propClass="t-max-w-96">
          <div className="t-line-clamp-2 t-break-words">
            {description || "-"}
          </div>
        </ConditionalToolTip>
      );
    },
    header: "Description",
    size: 16,
  }),
  columnHelper.accessor((row) => row.transaction, {
    id: "Amount",
    enableSorting: true,
    cell: (info) => {
      const { amount = 0 } = info.getValue();
      return <TxnAmount amount={Number(amount)} />;
    },
    header: () => <div className="t-flex t-justify-end">Amount</div>,
    size: 12,
  }),
  columnHelper.accessor((row) => row.transaction.information_request, {
    id: "RequestInformation",
    enableSorting: false,
    cell: RequestInput,
    header: () => (
      <div className="after:t-font-bold after:t-text-red after:t-content-['_*']">
        Request Information
      </div>
    ),
    size: 38,
  }),
  columnHelper.accessor((row) => row.transaction.information_request, {
    id: "Applytoall",
    header: "",
    cell: (info) => {
      const meta = info.table.options.meta as TableMetaType;
      if (meta.focusedItem === info.row.original.transaction.uuid) {
        return <ApplyToAll info={info} />;
      }
    },
    size: 4,
  }),
  columnHelper.display({
    id: "Delete",
    cell: (info) => (
      <DeleteColumn
        row={info.row.original}
        meta={info.table.options.meta as TableMetaType}
      />
    ),
    header: "",
    size: 4,
  }),
];

const SORT_COL_MAP: Record<string, string> = {
  Date: "DATE",
  Vendor: "VENDOR",
  Amount: "AMOUNT",
};

export const RequestInfo = ({
  isOpen,
  onClose,
  channelUrl,
  transactionIds,
  deselectRows,
  setRowSelection,
}: RequestInfoProps) => {
  const dispatch = useDispatch();
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [focusedItem, setFocusedItem] = useState<string | null>(null);
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSetSearch = useMemo(() => debounce(setSearch), []);
  const formRef = useRef<HTMLFormElement>(null);
  const [txnRequestInfo] = useAddOpenItemsMutation();

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const preventSubmit = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    form.addEventListener("submit", preventSubmit);
    return () => form.removeEventListener("submit", preventSubmit);
  }, []);

  const openItemFilters = useSelector(
    (state: RootState) => state.openItemFilter
  );

  const sortParams = useMemo((): OpenItemFilterParams => {
    if (!sorting.length) return {};
    const [{ id, desc }] = sorting;
    const sort_col = SORT_COL_MAP[id];
    if (!sort_col) return {};
    return { sort_col, sort_order: desc ? "DSC" : "ASC" };
  }, [sorting]);

  const filterParams: OpenItemFilterParams = {
    search_term: search || undefined,
    start_date: openItemFilters.startDate || undefined,
    end_date: openItemFilters.endDate || undefined,
    min_amount: openItemFilters.minAmount || undefined,
    max_amount: openItemFilters.maxAmount || undefined,
    cash_flow: openItemFilters.cashFlow || undefined,
    merchants: openItemFilters.vendors.length
      ? openItemFilters.vendors.join(",")
      : undefined,
    category_ids: openItemFilters.categoryIds.length
      ? openItemFilters.categoryIds.join(",")
      : undefined,
    from_ids: openItemFilters.fromIds.length
      ? openItemFilters.fromIds.join(",")
      : undefined,
    journal_entry: openItemFilters.manualEntry || undefined,
    bank_statement: openItemFilters.bankStatement || undefined,
    has_no_merchants: openItemFilters.hasNoVendors || undefined,
    ...sortParams,
  };

  const {
    data: requestedTxn,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetOpenItemTransactionsQuery(
    {
      groupId,
      entityId: entityId || "",
      transactionIds,
      ...filterParams,
    },
    {
      skip: !groupId || !transactionIds || !entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const hasLoadedOnce = useRef(false);
  if (isSuccess) hasLoadedOnce.current = true;
  const showFullLoader = isLoading && !hasLoadedOnce.current;

  const [sendRequestInfo] = useSendClarificationRequestMutation();
  const [addRequestInfo] = useAddClarificationMutation();

  const transactions = requestedTxn?.transactions || [];

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: { sorting },
    onSortingChange: setSorting,
    defaultColumn: { size: 10, minSize: 5, maxSize: 100 },
    meta: {
      setFocusedItem,
      focusedItem,
      setRowSelection,
      isLoading,
      isFetching,
      deletingRowId,
      setDeletingRowId,
    },
  });

  const handleClose = () => {
    dispatch(resetOpenItemFilter());
    onClose();
  };

  const onSubmit = async (values: {
    information_requests: Record<string, string>;
  }) => {
    try {
      const transactionRequestInfo = await createTransactionRequestInfo();
      const csvId = transactionRequestInfo?.csv_id;
      const itemTransactionIdMap = mapTransactionIdsToOpenItems(
        transactionRequestInfo
      );
      await addInformationRequests(values, itemTransactionIdMap);
      await sendRequest(csvId);
      deselectRows();
      handleClose();
      dispatch(setToOpenChatId(channelUrl));
      dispatch(openFloatingChat());
      successToast({ message: "Request sent" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const createTransactionRequestInfo = async () => {
    try {
      return await txnRequestInfo({
        groupId,
        transactionIds,
        channelUrl,
        customType: REQUESTED_INFO_CUSTOM_TYPE,
        cardStatus: "0",
      }).unwrap();
    } catch (error) {
      throw new Error((error as BackendError)?.data?.error?.message);
    }
  };

  const mapTransactionIdsToOpenItems = (transactionRequestInfo: any) => {
    return transactionRequestInfo.open_items.reduce(
      (acc: Record<string, string>, openItem: any) => {
        acc[openItem.transaction_id] = openItem.uuid;
        return acc;
      },
      {}
    );
  };

  const addInformationRequests = async (
    values: { information_requests: Record<string, string> },
    itemTransactionIdMap: Record<string, string>
  ) => {
    try {
      const entries = Object.entries(values.information_requests);
      await Promise.allSettled(
        entries.map(async ([uuid, request]) =>
          addRequestInfo({
            groupId,
            openItemId: itemTransactionIdMap[uuid],
            informationRequest: request,
          }).unwrap()
        )
      );
    } catch (error) {
      throw new Error((error as BackendError)?.data?.error?.message);
    }
  };

  const sendRequest = async (csvId: string) => {
    try {
      await sendRequestInfo({
        channelUrl,
        csvId,
        customType: REQUESTED_INFO_CUSTOM_TYPE,
        groupId,
      }).unwrap();
    } catch (error) {
      throw new Error((error as BackendError)?.data?.error?.message);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content
        size="fullscreen"
        asChild
        onEscapeKeyDown={(e) => {
          if (document.activeElement instanceof HTMLInputElement) {
            e.preventDefault();
          }
        }}
      >
        <FormikForm
          ref={formRef}
          enableReinitialize
          initialValues={{
            information_requests: Object.fromEntries(
              transactions.map((i) => [
                i.transaction.uuid,
                i.transaction.information_request || "",
              ])
            ),
          }}
          onSubmit={onSubmit}
          validateOnMount
          validationSchema={requestInfoValidation}
        >
          {({ isValid, submitForm, isSubmitting }) => (
            <>
              <Modal.Header>
                <div className="t-flex t-items-center t-gap-2">
                  <Modal.Title>Request Info</Modal.Title>
                  {!showFullLoader && (
                    <span className="t-rounded-full t-bg-neutral-10 t-px-2 t-py-0.5 t-text-caption t-text-text-60">
                      {transactions.length}{" "}
                      {transactions.length === 1 ? "item" : "items"}
                    </span>
                  )}
                </div>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body
                className="t-flex-1 t-min-h-0 t-flex t-flex-col t-gap-3"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (
                    e.key === "Enter" &&
                    e.target instanceof HTMLInputElement
                  ) {
                    e.preventDefault();
                  }
                }}
              >
                {showFullLoader ? (
                  <Loader />
                ) : (
                  <>
                    <div className="t-flex t-items-center t-gap-2">
                      <Search
                        placeholder="Search"
                        value={searchInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setSearchInput(e.target.value);
                          debouncedSetSearch(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault();
                        }}
                        className="t-w-72"
                      />
                      <OpenItemsFilter />
                    </div>
                    {!isFetching && transactions.length === 0 ? (
                      <div className="t-flex t-flex-1 t-items-center t-justify-center">
                        <TableEmptyState
                          icon={<EmptyHourGlass />}
                          title="No transactions found"
                          description={
                            searchInput
                              ? "Try adjusting your search or filters"
                              : "No open items to display"
                          }
                        />
                      </div>
                    ) : (
                      <TableUI
                        table={table}
                        isLoading={isFetching}
                        enableSort
                        layout="fixed"
                      />
                    )}
                  </>
                )}
              </Modal.Body>
              {!showFullLoader && (
                <Modal.Footer className="t-flex t-justify-end">
                  <Button
                    customType="primary"
                    disabled={!isValid || isSubmitting}
                    onClick={submitForm}
                    isLoading={isSubmitting}
                  >
                    Submit
                  </Button>
                </Modal.Footer>
              )}
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
